import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  Card,
  CardContent,
  Grid,
  Divider,
  TextField,
  CircularProgress,
  Chip,
  Snackbar
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  PlayArrow as PlayIcon,
  Assessment as AssessmentIcon,
  DeleteSweep as CleanupIcon
} from '@mui/icons-material';
import api from '../services/api';
import { useDateFormat } from '../hooks/useDateFormat';

interface JobStatus {
  name: string;
  lastRun: string | null;
  status: 'success' | 'error' | 'running' | null;
  error?: string;
}

const Metrics: React.FC = () => {
  const [jobsStatus, setJobsStatus] = useState<JobStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // Backfill form state
  const [backfillStartDate, setBackfillStartDate] = useState<string>('');
  const [backfillEndDate, setBackfillEndDate] = useState<string>('');
  const [backfillLoading, setBackfillLoading] = useState(false);
  
  // Trigger loading states
  const [trigger15MinLoading, setTrigger15MinLoading] = useState(false);
  const [triggerDailyLoading, setTriggerDailyLoading] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);

  const { formatDateTime } = useDateFormat();

  useEffect(() => {
    fetchJobStatus();
  }, []);

  const fetchJobStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getMetricsJobStatus();
      if (response.success && response.data) {
        setJobsStatus(response.data);
      } else {
        setError('Failed to fetch job status');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch job status');
      console.error('Error fetching job status:', err);
    } finally {
      setLoading(false);
    }
  };

  const getJobStatus = (name: string): JobStatus | undefined => {
    return jobsStatus.find(job => job.name.toLowerCase().includes(name.toLowerCase()));
  };

  const handleTrigger15Min = async () => {
    try {
      setTrigger15MinLoading(true);
      const response = await api.triggerMetrics15MinJob();
      if (response.success) {
        setSnackbar({ open: true, message: '15-minute job triggered successfully', severity: 'success' });
        await fetchJobStatus();
      } else {
        setSnackbar({ open: true, message: response.message || 'Failed to trigger job', severity: 'error' });
      }
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to trigger 15-minute job', severity: 'error' });
      console.error('Error triggering 15-minute job:', err);
    } finally {
      setTrigger15MinLoading(false);
    }
  };

  const handleTriggerDaily = async () => {
    try {
      setTriggerDailyLoading(true);
      const response = await api.triggerMetricsDailyJob();
      if (response.success) {
        setSnackbar({ open: true, message: 'Daily job triggered successfully', severity: 'success' });
        await fetchJobStatus();
      } else {
        setSnackbar({ open: true, message: response.message || 'Failed to trigger job', severity: 'error' });
      }
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to trigger daily job', severity: 'error' });
      console.error('Error triggering daily job:', err);
    } finally {
      setTriggerDailyLoading(false);
    }
  };

  const handleBackfill = async () => {
    if (!backfillStartDate || !backfillEndDate) {
      setSnackbar({ open: true, message: 'Please select both start and end dates', severity: 'error' });
      return;
    }

    try {
      setBackfillLoading(true);
      const response = await api.triggerMetricsBackfillJob(backfillStartDate, backfillEndDate);
      if (response.success) {
        setSnackbar({ open: true, message: 'Backfill job triggered successfully', severity: 'success' });
        setBackfillStartDate('');
        setBackfillEndDate('');
        await fetchJobStatus();
      } else {
        setSnackbar({ open: true, message: response.message || 'Failed to trigger backfill', severity: 'error' });
      }
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to trigger backfill job', severity: 'error' });
      console.error('Error triggering backfill:', err);
    } finally {
      setBackfillLoading(false);
    }
  };

  const handleCleanup = async () => {
    if (!window.confirm('This will merge duplicate dates and remove empty rows. Continue?')) {
      return;
    }

    try {
      setCleanupLoading(true);
      const response = await api.cleanupMetricsSheet();
      if (response.success) {
        const merged = response.data?.merged || 0;
        const removed = response.data?.removed || 0;
        setSnackbar({ 
          open: true, 
          message: `Cleanup completed: ${merged} dates merged, ${removed} rows removed`, 
          severity: 'success' 
        });
      } else {
        setSnackbar({ open: true, message: response.message || 'Failed to cleanup sheet', severity: 'error' });
      }
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to cleanup sheet', severity: 'error' });
      console.error('Error cleaning up sheet:', err);
    } finally {
      setCleanupLoading(false);
    }
  };

  const getStatusColor = (status: string | null) => {
    switch (status) {
      case 'running':
        return 'info';
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string | null) => {
    switch (status) {
      case 'running':
        return 'Running';
      case 'success':
        return 'Success';
      case 'error':
        return 'Error';
      default:
        return 'Not Run';
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    try {
      return formatDateTime(dateString);
    } catch {
      return dateString;
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <AssessmentIcon sx={{ mr: 2, fontSize: 32 }} />
        <Typography variant="h4" component="h1">
          Metrics Administration
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {/* Job Status Cards */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">15-Minute Job</Typography>
                  <Button
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={fetchJobStatus}
                    disabled={loading}
                  >
                    Refresh
                  </Button>
                </Box>
                <Divider sx={{ mb: 2 }} />
                {(() => {
                  const job = getJobStatus('15-minute');
                  return job ? (
                    <>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Status:
                        </Typography>
                        <Chip
                          label={getStatusLabel(job.status)}
                          color={getStatusColor(job.status) as any}
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Last Run:
                        </Typography>
                        <Typography variant="body1">
                          {formatDate(job.lastRun)}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Schedule:
                        </Typography>
                        <Typography variant="body1">
                          Every 15 minutes
                        </Typography>
                      </Box>
                      {job.error && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Last Error:
                          </Typography>
                          <Typography variant="body2" color="error">
                            {job.error}
                          </Typography>
                        </Box>
                      )}
                      <Button
                        variant="contained"
                        startIcon={trigger15MinLoading ? <CircularProgress size={16} /> : <PlayIcon />}
                        onClick={handleTrigger15Min}
                        disabled={trigger15MinLoading}
                        fullWidth
                        sx={{ mt: 2 }}
                      >
                        Trigger Now
                      </Button>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Job status not available
                    </Typography>
                  );
                })()}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Daily Job</Typography>
                  <Button
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={fetchJobStatus}
                    disabled={loading}
                  >
                    Refresh
                  </Button>
                </Box>
                <Divider sx={{ mb: 2 }} />
                {(() => {
                  const job = getJobStatus('daily');
                  return job ? (
                    <>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Status:
                        </Typography>
                        <Chip
                          label={getStatusLabel(job.status)}
                          color={getStatusColor(job.status) as any}
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Last Run:
                        </Typography>
                        <Typography variant="body1">
                          {formatDate(job.lastRun)}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Schedule:
                        </Typography>
                        <Typography variant="body1">
                          Daily at 2:00 AM
                        </Typography>
                      </Box>
                      {job.error && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Last Error:
                          </Typography>
                          <Typography variant="body2" color="error">
                            {job.error}
                          </Typography>
                        </Box>
                      )}
                      <Button
                        variant="contained"
                        startIcon={triggerDailyLoading ? <CircularProgress size={16} /> : <PlayIcon />}
                        onClick={handleTriggerDaily}
                        disabled={triggerDailyLoading}
                        fullWidth
                        sx={{ mt: 2 }}
                      >
                        Trigger Now
                      </Button>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Job status not available
                    </Typography>
                  );
                })()}
              </CardContent>
            </Card>
          </Grid>

          {/* Backfill Section */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Backfill Metrics
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Start Date"
                      type="date"
                      value={backfillStartDate}
                      onChange={(e) => setBackfillStartDate(e.target.value)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="End Date"
                      type="date"
                      value={backfillEndDate}
                      onChange={(e) => setBackfillEndDate(e.target.value)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleBackfill}
                      disabled={backfillLoading || !backfillStartDate || !backfillEndDate}
                      startIcon={backfillLoading ? <CircularProgress size={16} /> : <PlayIcon />}
                    >
                      Run Backfill
                    </Button>
                  </Grid>
                </Grid>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Calculate and update metrics for a date range. This will process all dates between the start and end date (inclusive).
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Cleanup Section */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Sheet Cleanup</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Clean up the Google Sheet by merging duplicate dates and removing empty rows. 
                  This will consolidate metrics for the same date into a single row.
                </Typography>
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={cleanupLoading ? <CircularProgress size={16} /> : <CleanupIcon />}
                  onClick={handleCleanup}
                  disabled={cleanupLoading}
                >
                  Cleanup Sheet
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Metrics;

